import React, { useState, useEffect } from 'react'
import { Shield, Plus, Trash2, Check, X, RefreshCw, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

const API = process.env.REACT_APP_BACKEND_URL

const MODE_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'assurance', label: 'Assurance' },
  { value: 'amiable', label: 'Amiable' },
  { value: 'retention', label: 'Retention' },
]

const RESP_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'responsable', label: 'Responsable' },
  { value: 'non_responsable', label: 'Non responsable' },
  { value: 'partage', label: 'Partage' },
]

export default function ReglesConservation({ tenantId }) {
  const [regles, setRegles] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [newRegle, setNewRegle] = useState({ nom: '', mode_gestion: '', responsabilite: '', consomme_conservation: false, inclus_sp: true, cout_inferieur_franchise: false, priorite: 10 })

  async function fetchRegles() {
    setLoading(true)
    try {
      const res = await fetch(`${API}/api/regles-conservation/${tenantId}`)
      const data = await res.json()
      setRegles(data.regles || [])
    } catch { toast.error('Erreur chargement regles') }
    finally { setLoading(false) }
  }

  useEffect(() => { if (tenantId) fetchRegles() }, [tenantId])

  async function initDefaut() {
    const res = await fetch(`${API}/api/regles-conservation/${tenantId}/init-defaut`, { method: 'POST' })
    const data = await res.json()
    toast.success(data.message)
    fetchRegles()
  }

  async function addRegle() {
    if (!newRegle.nom) { toast.error('Nom requis'); return }
    const res = await fetch(`${API}/api/regles-conservation`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...newRegle, tenant_id: tenantId }),
    })
    if (res.ok) { setAdding(false); setNewRegle({ nom: '', mode_gestion: '', responsabilite: '', consomme_conservation: false, inclus_sp: true, cout_inferieur_franchise: false, priorite: 10 }); fetchRegles(); toast.success('Regle ajoutee') }
    else toast.error('Erreur')
  }

  async function toggleRegle(id, field, current) {
    const res = await fetch(`${API}/api/regles-conservation/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: !current }),
    })
    if (res.ok) fetchRegles()
  }

  async function deleteRegle(id) {
    if (!window.confirm('Supprimer cette regle ?')) return
    await fetch(`${API}/api/regles-conservation/${id}`, { method: 'DELETE' })
    fetchRegles()
  }

  const checkCls = "w-5 h-5 rounded cursor-pointer flex items-center justify-center transition-colors"

  return (
    <div data-testid="regles-conservation">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-sm text-slate-800 flex items-center gap-2">
          <Shield size={16} className="text-indigo-500" />
          Regles de conservation
        </h3>
        <div className="flex gap-2">
          {regles.length === 0 && (
            <button onClick={initDefaut} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors" data-testid="init-defaut-btn">
              <RefreshCw size={12} /> Initialiser par defaut
            </button>
          )}
          <button onClick={() => setAdding(!adding)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors" data-testid="add-regle-btn">
            <Plus size={12} /> Ajouter
          </button>
        </div>
      </div>

      {adding && (
        <div className="bg-indigo-50 rounded-lg p-4 mb-4 space-y-3 border border-indigo-200">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] text-slate-500 block mb-0.5">Nom *</label>
              <input type="text" value={newRegle.nom} onChange={e => setNewRegle(r => ({ ...r, nom: e.target.value }))}
                className="w-full h-8 px-2 text-xs border rounded outline-none focus:ring-1 focus:ring-indigo-400" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 block mb-0.5">Mode gestion</label>
              <select value={newRegle.mode_gestion} onChange={e => setNewRegle(r => ({ ...r, mode_gestion: e.target.value }))}
                className="w-full h-8 px-2 text-xs border rounded bg-white">
                {MODE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 block mb-0.5">Responsabilite</label>
              <select value={newRegle.responsabilite} onChange={e => setNewRegle(r => ({ ...r, responsabilite: e.target.value }))}
                className="w-full h-8 px-2 text-xs border rounded bg-white">
                {RESP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={newRegle.consomme_conservation} onChange={e => setNewRegle(r => ({ ...r, consomme_conservation: e.target.checked }))} /> Consomme conservation</label>
            <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={newRegle.inclus_sp} onChange={e => setNewRegle(r => ({ ...r, inclus_sp: e.target.checked }))} /> Inclus S/P</label>
            <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={newRegle.cout_inferieur_franchise} onChange={e => setNewRegle(r => ({ ...r, cout_inferieur_franchise: e.target.checked }))} /> Cout &lt; Franchise</label>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setAdding(false)} className="px-3 py-1 text-xs text-slate-500 hover:bg-slate-100 rounded"><X size={12} /></button>
            <button onClick={addRegle} className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-1"><Check size={12} /> Creer</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin text-slate-400" /></div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b">
                {['Regle', 'Mode gestion', 'Responsabilite', 'Cout < Franchise', 'Consomme', 'Inclus S/P', ''].map(h => (
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {regles.map((r, i) => (
                <tr key={r.id} className={`border-b border-slate-100 ${i % 2 ? 'bg-slate-50/30' : ''} ${!r.actif ? 'opacity-40' : ''}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{r.nom}</td>
                  <td className="px-3 py-2 text-slate-500">{r.mode_gestion || 'Tous'}</td>
                  <td className="px-3 py-2 text-slate-500">{r.responsabilite || 'Tous'}</td>
                  <td className="px-3 py-2">
                    <div onClick={() => toggleRegle(r.id, 'cout_inferieur_franchise', r.cout_inferieur_franchise)}
                      className={`${checkCls} ${r.cout_inferieur_franchise ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-300'}`}>
                      {r.cout_inferieur_franchise && <Check size={12} />}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div onClick={() => toggleRegle(r.id, 'consomme_conservation', r.consomme_conservation)}
                      className={`${checkCls} ${r.consomme_conservation ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                      {r.consomme_conservation ? <Check size={12} /> : <X size={12} />}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div onClick={() => toggleRegle(r.id, 'inclus_sp', r.inclus_sp)}
                      className={`${checkCls} ${r.inclus_sp ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-300'}`}>
                      {r.inclus_sp && <Check size={12} />}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => deleteRegle(r.id)} className="p-1 text-slate-300 hover:text-red-500 transition-colors" data-testid="delete-regle-btn">
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
              {regles.length === 0 && (
                <tr><td colSpan="7" className="px-3 py-6 text-center text-slate-400">Aucune regle. Cliquez "Initialiser par defaut".</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
